import styled from '@emotion/styled';
import { Button } from 'antd';
import { useMemo, useRef, useState, ReactNode, useEffect } from 'react';
import { TFnType, useTranslate } from '@tolgee/react';
import { noop } from 'lodash-es';
import { DeleteOutlined } from '@ant-design/icons';
import UploadEditor from '@knowledge/common/components/UploadEditor';
import {
  descendants2segments,
  getTextLength,
  isEmptyDescendants,
  segments2descendants,
} from '@knowledge/common/components/UploadEditor/utils';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { Descendant } from 'slate';
import isEqual from 'react-fast-compare';
import { isURL } from 'utils/is';

type KnowledgeContentProps = {
  value?: Knowledge.SPU.Segment[];
  onChange?: (value: Knowledge.SPU.Segment[]) => void;
  maxLength?: number;
  shortcut?: boolean; // 快捷模式，方便在知识库列表中快速编辑
  onOk?: (value?: { segments: Knowledge.SPU.Segment[] }) => void; // 快捷模式
  onCancel?: () => void; // 快捷模式
  lighten?: boolean; // 头部白色背景
  required?: boolean; // 是否必填
  onDelete?: () => void;
  showHeader?: boolean;
  loading?: boolean;
  borderless?: boolean;
  headerRightExtraNode?: ReactNode;
  toolbarExtraNode?: ReactNode;
  setDefaultNode?: ReactNode;
  isDirty?: boolean;
};

function KnowledgeContentItem(props: KnowledgeContentProps) {
  const {
    value: valueProp,
    onChange,
    maxLength = 6000,
    onCancel = noop,
    onOk = noop,
    onDelete,
    required = true,
    shortcut,
    showHeader = false,
    loading,
    borderless = false,
    headerRightExtraNode = null,
    toolbarExtraNode = null,
    setDefaultNode = null,
    isDirty = false,
  } = props;
  const { t } = useTranslate();

  const [value, setValue] = useState(() => segments2descendants(valueProp));
  // 需要外部受控
  useEffect(() => {
    // setValue(segments2descendants(valueProp));
  }, [valueProp]);

  const initialValueRef = useRef(value);

  const okDisabled = useMemo(() => {
    return isEmptyDescendants(value) || isEqual(value, initialValueRef.current);
  }, [value]);

  const error = useMemo(() => {
    if (!shortcut) {
      return undefined;
    }

    return validateUploadValue(value, t, maxLength);
  }, [shortcut, value, maxLength, t]);

  const [touched, setTouched] = useState(false);

  const component = (
    <Container>
      <Block>
        {showHeader && (
          <Header>
            {required && <span className="required">*</span>}
            {t('knowledge.知识值')}
          </Header>
        )}
        <UploadEditor
          value={value}
          onChange={(descendants) => {
            setValue(descendants);
            onChange?.(descendants2segments(descendants));
          }}
          maxLength={maxLength}
          headerRightExtraNode={headerRightExtraNode}
          toolbarExtraNode={toolbarExtraNode}
          style={
            borderless
              ? {
                  border: 'none',
                }
              : touched && error
              ? {
                  borderColor: 'var(--ant-error-color)',
                }
              : undefined
          }
        />
        {touched && (shortcut || borderless) && (
          <span style={{ color: 'var(--ant-error-color)' }}>{error}</span>
        )}
      </Block>
    </Container>
  );

  return shortcut ? (
    <ShortcutContainer>
      {component}
      <div className="flex mt-[11px] justify-between">
        <div className="flex items-center">
          <Button
            type="primary"
            onClick={() => {
              if (error) {
                setTouched(true);
              } else {
                if (okDisabled) {
                  onOk();
                } else {
                  onOk({ segments: descendants2segments(value) });
                }
              }
            }}
            loading={loading}
            disabled={isDirty ? false : okDisabled}
          >
            {t('phrase.保存')}
          </Button>
          <Button onClick={onCancel} type="link">
            {t('phrase.取消')}
          </Button>
          {setDefaultNode}
        </div>
        {onDelete && (
          <div>
            <Button onClick={onDelete} type="text" icon={<DeleteOutlined />}>
              {t('marketing.删除')}
            </Button>
          </div>
        )}
      </div>
    </ShortcutContainer>
  ) : (
    component
  );
}

export function validateUploadValue(value: Descendant[], t: TFnType, maxLength = 600) {
  if (isEmptyDescendants(value)) {
    return t('Entities.请输入');
  }

  if (getTextLength(value) > maxLength) {
    return t('cannot_exceed_x_characters', { x: maxLength });
  }

  if (value.some((cur) => cur.type === CustomSlateNodeType.UPLOAD && !isURL(cur.id))) {
    return t('upload_validate_tips');
  }
}

const Container = styled.div`
  flex: 1 0 0;
  overflow: hidden;
`;
const Block = styled.div`
  height: 100%;
`;

const Header = styled.header`
  height: 40px;
  display: flex;
  align-items: center;
  span.required {
    color: #ff4d4f;
    padding: 0 4px;
    font-size: 14px;
    font-family: SimSun, sans-serif;
    line-height: 1;
  }
`;
const ShortcutContainer = styled.div`
  padding: 12px;
  background: #f4f6f8;
  border-radius: 4px;
`;

export default KnowledgeContentItem;
